import React, { useMemo } from 'react';
import { Box, Grid, useResponsive } from '@common-fe/common-fe';

import useFileErrorPopup from '@/components/controls/DropScan/hooks/useFileErrorPopup';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import { ICreateDocumentAggregatorDtoDocumentTypeEnum } from '@/dtos';
import { useGetReceiptsQuery } from '@/modules/MyDocuments/queries';
import useGetDocumentByQRCodeQuery from '@/modules/transaction/hooks/useGetDocumentByQRCode.query';
import useDeleteDocumentQuery from '@/modules/transaction/queries/useDeleteDocument.query';
import { UseMyMoneyDropScan } from '@/modules/UseMyMoney/components';
import { useMyMoneySavedReceiptsFeatureFlag } from '@/modules/UseMyMoney/hooks/useMyMoneySavedReceiptsFeatureFlag';
import { useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import { UseMyMoneyOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import useSetFileFromQrCode from '@/modules/UseMyMoney/widgets/QrCode/hooks/useSetFileFromQrCode';
import UploadFileQRCodeContainer from '@/modules/UseMyMoney/widgets/QrCode/QrCode.container';
import useQRCodeStore from '@/modules/UseMyMoney/widgets/QrCode/stores/useQRCode.store';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import theme from '@/styles/theme';

import { SavedReceiptsCard, UploadFromDeviceCard, UploadViaQrCodeCard } from './components';

export interface UploadFileContainerProps {
  storeKey: string;
  onUpload: () => void;
  onQrCodeUpload: () => void;
  disableFilesUpload?: boolean;
  documentType?: ICreateDocumentAggregatorDtoDocumentTypeEnum;
  onSelectFromSavedReceipts?: () => void;
  disableDragAndDrop?: boolean;
  disableRemoveDocumentOnError?: boolean;
}

const UploadFileContainer: React.FC<UploadFileContainerProps> = ({
  storeKey,
  onUpload,
  onQrCodeUpload,
  disableFilesUpload,
  documentType,
  onSelectFromSavedReceipts,
  disableDragAndDrop,
  disableRemoveDocumentOnError,
}) => {
  const { isUseMyMoneySavedReceiptsEnabled } = useMyMoneySavedReceiptsFeatureFlag();
  const { isMobile } = useResponsive();
  const { employee } = useEmployeeStore();
  const { useMyMoneyOption } = useMyMoneyStore();
  const { receipts, isReceiptsLoading } = useGetReceiptsQuery({
    employeeId: employee?.id,
    page: 0,
    perPage: 3,
  });

  const { state: { qrCodeId } } = useQRCodeStore();
  const { document } = useGetDocumentByQRCodeQuery({ qrCodeId });
  useSetFileFromQrCode({ storeKey, document, onFileReceivedByQrCode: onQrCodeUpload });

  const { deleteDocument } = useDeleteDocumentQuery();

  const {
    fileUploadError,
    isFileUploadErrorModalVisible,
    onCloseFileUploadError,
    onFileUploadError,
  } = useFileErrorPopup();

  const isOcrDisabled = useMemo(() => useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL, [useMyMoneyOption]);
  
  return (
    <>
      {disableDragAndDrop ? null : (
        <UseMyMoneyDropScan
          storeKey={storeKey}
          onUpload={onUpload}
          onFileUploadError={onFileUploadError}
          deleteDocument={disableRemoveDocumentOnError ? undefined : deleteDocument}
        />
      )}

      <Box data-testid="upload-file_container">
        <Grid columns={isMobile ? ['1fr'] : ['1fr', '1fr']} gap="spacing12">
          <UploadFromDeviceCard
            storeKey={storeKey}
            isMobile={isMobile}
            onUpload={onUpload}
            onFileUploadError={onFileUploadError}
            disableFilesUpload={disableFilesUpload}
            isOcrDisabled={isOcrDisabled}
          />
          {isMobile ? null : (
            <UploadViaQrCodeCard>
              <UploadFileQRCodeContainer
                storeKey={storeKey}
                bgColor={theme.colors.module}
                documentType={documentType}
              />
            </UploadViaQrCodeCard>
          )}
        </Grid>

        {isUseMyMoneySavedReceiptsEnabled ? (
          <SavedReceiptsCard
            isMobile={isMobile}
            receipts={receipts}
            isReceiptsLoading={isReceiptsLoading}
            onSelectFromSavedReceipts={onSelectFromSavedReceipts}
          />
        ) : null}
      </Box>

      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup fileError={fileUploadError} onClose={onCloseFileUploadError} />
      )}
    </>
  );
};

export default UploadFileContainer;
